import { sql } from "drizzle-orm";
import { type AnyPgColumn, boolean as pgBoolean, check, foreignKey, index, integer, jsonb, numeric, pgEnum, pgTable, serial, text, timestamp, uniqueIndex } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";
import { DECIMAL_CONFIG, computedDecimalSchema, computedDecimalText, decimalCompare, nullableDecimalSchema, positiveDecimalSchema, type DecimalValue } from "../values/decimal";
import type { DeliveryEntry } from "../orders/order-delivery-schedule";
import { ORDER_HISTORY_EVENT_VALUES, type OrderHistoryEventType } from "../orders/order-history-events";
import { ORDER_DIRECTION_VALUES, ORDER_STATUS_VALUES, ORDER_STATUSES } from "../orders/order-types";
import { CONTACT_TYPE_VALUES } from "./domain-values";

export const tenants = pgTable("tenants", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  stripeCustomerId: text("stripe_customer_id"),
  stripeSubscriptionId: text("stripe_subscription_id"),
  subscriptionStatus: text("subscription_status").notNull().default("inactive"),
  createdAt: timestamp("created_at").defaultNow(),
});

export type Tenant = typeof tenants.$inferSelect;

export const users = pgTable("users", {
  id: serial("id").primaryKey(),
  supabase_id: text("supabase_id").unique(),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  role: text("role").default("chemist").notNull(),
  name: text("name").notNull(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
  lastLoginAt: timestamp("last_login_at"),
});

export const insertUserSchema = createInsertSchema(users).pick({
  email: true,
  password: true,
  name: true,
  role: true,
  supabase_id: true,
  tenantId: true,
  lastLoginAt: true,
});

export type InsertUser = z.infer<typeof insertUserSchema>;
export type User = typeof users.$inferSelect;

export const contactTypeSchema = z.enum(CONTACT_TYPE_VALUES);
export type ContactType = z.infer<typeof contactTypeSchema>;
export const contactTypeEnum = pgEnum("contact_type", CONTACT_TYPE_VALUES);

export const contacts = pgTable("contacts", {
  id: serial("id").primaryKey(),
  company: text("company").notNull(),
  contactPerson: text("contact_person"),
  contactType: contactTypeEnum("contact_type").notNull(),
  email: text("email"),
  phone: text("phone"),
  address: text("address"),
  notes: text("notes"),
  country: text("country"),
  createdBy: integer("created_by").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  modifiedBy: integer("modified_by").notNull(),
  modifiedAt: timestamp("modified_at").notNull().defaultNow(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
});

export const CONTACT_COUNTRY_MAX_LENGTH = 100;

export const insertContactSchema = createInsertSchema(contacts)
  .omit({
    id: true,
    createdAt: true,
    modifiedAt: true,
  })
  .extend({
    contactType: contactTypeSchema,
    country: z
      .string()
      .trim()
      .max(CONTACT_COUNTRY_MAX_LENGTH)
      .transform((value) => value || null)
      .nullish(),
  });

export type InsertContact = z.infer<typeof insertContactSchema>;
export type Contact = typeof contacts.$inferSelect;

export const addresses = pgTable("addresses", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
  contactId: integer("contact_id").references(() => contacts.id, { onDelete: "cascade" }),
  label: text("label").notNull(),
  lines: text("lines").notNull(),
  country: text("country").notNull().default(""),
  defaultBilling: pgBoolean("default_billing").notNull().default(false),
  defaultShipping: pgBoolean("default_shipping").notNull().default(false),
  archivedAt: timestamp("archived_at"),
  createdBy: integer("created_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
}, (table) => ({
  oneCompanyDefaultBilling: uniqueIndex("addresses_one_default_billing").on(table.tenantId).where(sql`default_billing AND archived_at IS NULL AND contact_id IS NULL`),
  oneCompanyDefaultShipping: uniqueIndex("addresses_one_default_shipping").on(table.tenantId).where(sql`default_shipping AND archived_at IS NULL AND contact_id IS NULL`),
  oneContactDefaultBilling: uniqueIndex("addresses_one_contact_default_billing").on(table.contactId).where(sql`default_billing AND archived_at IS NULL`),
  oneContactDefaultShipping: uniqueIndex("addresses_one_contact_default_shipping").on(table.contactId).where(sql`default_shipping AND archived_at IS NULL`),
  tenantIdx: index("addresses_tenant_id_idx").on(table.tenantId),
  contactIdx: index("addresses_contact_id_idx").on(table.contactId),
}));

export type Address = typeof addresses.$inferSelect;

export const contactPeople = pgTable("contact_people", {
  id: serial("id").primaryKey(),
  contactId: integer("contact_id")
    .notNull()
    .references(() => contacts.id, { onDelete: "cascade" }),
  name: text("name").notNull(),
  email: text("email"),
  phone: text("phone"),
  role: text("role"),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
});

export const insertContactPersonSchema = createInsertSchema(contactPeople).omit({
  id: true,
  createdAt: true,
});

export type InsertContactPerson = z.infer<typeof insertContactPersonSchema>;
export type ContactPerson = typeof contactPeople.$inferSelect;

export const suppliers = pgTable("suppliers", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  contactPerson: text("contact_person"),
  email: text("email"),
  phone: text("phone"),
  address: text("address"),
  notes: text("notes"),
  createdBy: integer("created_by").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  modifiedBy: integer("modified_by").notNull(),
  modifiedAt: timestamp("modified_at").notNull().defaultNow(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
});

export const insertSupplierSchema = createInsertSchema(suppliers).omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
});

export type InsertSupplier = z.infer<typeof insertSupplierSchema>;
export type Supplier = typeof suppliers.$inferSelect;

export const purchasers = pgTable("purchasers", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  contactPerson: text("contact_person"),
  email: text("email"),
  phone: text("phone"),
  address: text("address"),
  notes: text("notes"),
  createdBy: integer("created_by").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  modifiedBy: integer("modified_by").notNull(),
  modifiedAt: timestamp("modified_at").notNull().defaultNow(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
});

export const insertPurchaserSchema = createInsertSchema(purchasers).omit({
  id: true,
  createdAt: true,
  modifiedAt: true,
});

export type InsertPurchaser = z.infer<typeof insertPurchaserSchema>;
export type Purchaser = typeof purchasers.$inferSelect;

const storedDecimalSchema = computedDecimalSchema.transform(computedDecimalText);

function positiveStoredQuantity(value: string): boolean {
  return decimalCompare(value, 0) > 0;
}

const positiveStoredQuantitySchema = storedDecimalSchema.pipe(z.string().refine(positiveStoredQuantity, DECIMAL_CONFIG.positive));

export const CHEMICAL_BATCH_NUMBER_UNIQUE = "chemicals_tenant_batch_number_unique";

export const chemicals = pgTable("chemicals", {
  id: serial("id").primaryKey(),
  productNumber: text("product_number").notNull(),
  name: text("name").notNull(),
  casNumber: text("cas_number").notNull(),
  batchNumber: text("batch_number"),
  location: text("location").notNull(),
  amount: numeric("amount").$type<DecimalValue>().notNull(),
  unit: text("unit").notNull(),
  costPerUnit: numeric("cost_per_unit").$type<DecimalValue>(),
  modifiedBy: integer("modified_by").notNull(),
  modifiedDate: timestamp("modified_date").notNull().defaultNow(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
  parentChemicalId: integer("parent_chemical_id"),
  splitTransactionId: integer("split_transaction_id"),
}, (table) => {
  return {
    tenantBatchNumberUnique: uniqueIndex(CHEMICAL_BATCH_NUMBER_UNIQUE).on(table.tenantId, table.batchNumber),
    parentChemicalIdFk: foreignKey({
      columns: [table.parentChemicalId],
      foreignColumns: [table.id],
    }).onDelete("set null"),
  };
});

export const insertChemicalSchema = createInsertSchema(chemicals, { amount: storedDecimalSchema, costPerUnit: nullableDecimalSchema.optional() }).omit({
  id: true,
  modifiedDate: true,
});

export type InsertChemical = z.input<typeof insertChemicalSchema>;
export type Chemical = typeof chemicals.$inferSelect & { modifiedByName?: string | null; hasDocuments?: boolean };

export const inventoryTransactions = pgTable("inventory_transactions", {
  id: serial("id").primaryKey(),
  chemicalId: integer("chemical_id").notNull(),
  transactionType: text("transaction_type").notNull(),
  inventoryApplied: pgBoolean("inventory_applied").notNull().default(true),
  quantity: numeric("quantity").$type<DecimalValue>().notNull(),
  finalCountedAmount: numeric("final_counted_amount").$type<DecimalValue>(),
  unit: text("unit").notNull(),
  transactionDate: timestamp("transaction_date").notNull().defaultNow(),
  supplierId: integer("supplier_id"),
  supplierContactId: integer("supplier_contact_id"),
  purchaserId: integer("purchaser_id"),
  purchaserContactId: integer("purchaser_contact_id"),
  price: numeric("price").$type<DecimalValue>(),
  referenceNumber: text("reference_number"),
  supplierBatchNumber: text("supplier_batch_number"),
  usageDetails: text("usage_details"),
  disposalReason: text("disposal_reason"),
  notes: text("notes"),
  createdBy: integer("created_by").notNull(),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
}, (table) => {
  return {
    chemicalIdFk: foreignKey({
      columns: [table.chemicalId],
      foreignColumns: [chemicals.id]
    }),
    supplierIdFk: foreignKey({
      columns: [table.supplierId],
      foreignColumns: [suppliers.id]
    }).onDelete("set null"),
    supplierContactIdFk: foreignKey({
      columns: [table.supplierContactId],
      foreignColumns: [contacts.id]
    }).onDelete("set null"),
    purchaserIdFk: foreignKey({
      columns: [table.purchaserId],
      foreignColumns: [purchasers.id]
    }).onDelete("set null"),
    purchaserContactIdFk: foreignKey({
      columns: [table.purchaserContactId],
      foreignColumns: [contacts.id]
    }).onDelete("set null"),
    createdByFk: foreignKey({
      columns: [table.createdBy],
      foreignColumns: [users.id]
    })
  };
});

export const insertInventoryTransactionSchema = createInsertSchema(inventoryTransactions, { quantity: storedDecimalSchema, finalCountedAmount: nullableDecimalSchema.optional(), price: nullableDecimalSchema.optional() }).omit({
  id: true,
  createdAt: true,
});

export type InsertInventoryTransaction = z.input<typeof insertInventoryTransactionSchema>;
export type InventoryTransaction = typeof inventoryTransactions.$inferSelect;

export const changeHistory = pgTable("change_history", {
  id: serial("id").primaryKey(),
  chemicalId: integer("chemical_id").notNull(),
  userId: integer("user_id").notNull(),
  changeDate: timestamp("change_date").notNull().defaultNow(),
  changeType: text("change_type").notNull(),
  fieldName: text("field_name"),
  oldValue: text("old_value"),
  newValue: text("new_value"),
  transactionId: integer("transaction_id"),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
}, (table) => {
  return {
    chemicalIdFk: foreignKey({
      columns: [table.chemicalId],
      foreignColumns: [chemicals.id]
    }),
    userIdFk: foreignKey({
      columns: [table.userId],
      foreignColumns: [users.id]
    }),
    transactionIdFk: foreignKey({
      columns: [table.transactionId],
      foreignColumns: [inventoryTransactions.id]
    }).onDelete("set null")
  };
});

export const insertChangeHistorySchema = createInsertSchema(changeHistory).omit({
  id: true,
  changeDate: true,
});

export type InsertChangeHistory = z.infer<typeof insertChangeHistorySchema>;

export const orderDirectionEnum = pgEnum("order_direction", ORDER_DIRECTION_VALUES);
export const orderStatusEnum = pgEnum("order_status", ORDER_STATUS_VALUES);

export const orders = pgTable("orders", {
  id: serial("id").primaryKey(),
  chemicalId: integer("chemical_id").references(() => chemicals.id),
  chemicalName: text("chemical_name"),
  casNumber: text("cas_number"),
  contactId: integer("contact_id").notNull().references(() => contacts.id),
  direction: orderDirectionEnum("direction").notNull(),
  quantity: numeric("quantity").$type<DecimalValue>().notNull(),
  unit: text("unit").notNull(),
  pricePerItem: numeric("price_per_item").$type<DecimalValue>(),
  internalProductNumber: text("internal_product_number"),
  externalProductNumber: text("external_product_number"),
  referenceNumber: text("reference_number"),
  customerPoNumber: text("customer_po_number"),
  contactAddressId: integer("contact_address_id").references((): AnyPgColumn => addresses.id),
  orderStatus: orderStatusEnum("order_status").notNull().default(ORDER_STATUSES.requested),
  submittedBy: integer("submitted_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  submittedAt: timestamp("submitted_at").notNull().defaultNow(),
  requestedBy: integer("requested_by").references(() => users.id),
  customerRequestorContactPersonId: integer("customer_requestor_contact_person_id")
    .references(() => contactPeople.id, { onDelete: "set null" }),
  orderedBy: integer("ordered_by").references(() => users.id),
  orderedAt: timestamp("ordered_at"),
  expectedDeliveryDate: timestamp("expected_delivery_date"),
  deliverySchedule: jsonb("delivery_schedule").$type<DeliveryEntry[]>().notNull().default([]),
  arrivedDate: timestamp("arrived_date"),
  notes: text("notes"),
  internalBatchNumber: text("internal_batch_number"),
  externalBatchNumber: text("external_batch_number"),
  cancellationReason: text("cancellation_reason"),
  returnedAt: timestamp("returned_at"),
  returnNotes: text("return_notes"),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
}, (table) => ({
  customerRequestorContactPersonIdx: index("orders_customer_requestor_contact_person_id_idx")
    .on(table.customerRequestorContactPersonId),
}));

export const insertOrderSchema = createInsertSchema(orders, { quantity: positiveStoredQuantitySchema, pricePerItem: nullableDecimalSchema.optional() }).omit({
  id: true,
  createdAt: true,
  submittedAt: true,
});

export type InsertOrder = z.input<typeof insertOrderSchema>;
export type Order = typeof orders.$inferSelect & { hasDocuments?: boolean };
export type OrderWithSummaries = Order & { paidTotal: DecimalValue; movedQuantity: DecimalValue };

export const orderHistory = pgTable("order_history", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id").notNull().references(() => orders.id, { onDelete: "cascade" }),
  eventType: text("event_type").$type<OrderHistoryEventType>().notNull(),
  userId: integer("user_id").notNull().references(() => users.id),
  eventDate: timestamp("event_date").notNull().defaultNow(),
  recordedAt: timestamp("recorded_at").notNull().defaultNow(),
  detail: jsonb("detail"),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
});

export const insertOrderHistorySchema = createInsertSchema(orderHistory, { eventType: z.enum(ORDER_HISTORY_EVENT_VALUES) }).omit({
  id: true,
  eventDate: true,
  recordedAt: true,
});

export type InsertOrderHistory = z.infer<typeof insertOrderHistorySchema>;
export type OrderHistory = typeof orderHistory.$inferSelect;

export const orderImportBatches = pgTable("order_import_batches", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
  fileHash: text("file_hash").notNull(),
  createdBy: integer("created_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  rowCount: integer("row_count").notNull(),
}, (table) => ({
  tenantFileHashIdx: uniqueIndex("order_import_batches_tenant_file_hash_idx").on(
    table.tenantId,
    table.fileHash,
  ),
}));

export const insertOrderImportBatchSchema = createInsertSchema(orderImportBatches).omit({
  id: true,
  createdAt: true,
});

export type InsertOrderImportBatch = z.infer<typeof insertOrderImportBatchSchema>;
export type OrderImportBatch = typeof orderImportBatches.$inferSelect;

export const orderPayments = pgTable("order_payments", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id").notNull().references(() => orders.id, { onDelete: "cascade" }),
  amount: numeric("amount").$type<DecimalValue>().notNull(),
  paymentDate: timestamp("payment_date").notNull(),
  invoiceNumber: text("invoice_number"),
  invoiceDate: timestamp("invoice_date"),
  notes: text("notes"),
  createdBy: integer("created_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
}, (table) => ({
  amountPositive: check("order_payments_amount_positive", sql`${table.amount} > 0`),
  orderIdx: index("order_payments_order_id_idx").on(table.orderId),
}));

export const insertOrderPaymentSchema = createInsertSchema(orderPayments, { amount: positiveDecimalSchema }).omit({ id: true, createdAt: true });
export type InsertOrderPayment = z.input<typeof insertOrderPaymentSchema>;
export type OrderPayment = typeof orderPayments.$inferSelect;

export const orderMovements = pgTable("order_movements", {
  id: serial("id").primaryKey(),
  orderId: integer("order_id").notNull().references(() => orders.id, { onDelete: "cascade" }),
  movementDate: timestamp("movement_date").notNull(),
  carrier: text("carrier"),
  trackingNumber: text("tracking_number"),
  addressId: integer("address_id").references(() => addresses.id),
  shippingHandlingDutyCharges: numeric("shipping_handling_duty_charges").$type<DecimalValue>(),
  warehouseCharges: numeric("warehouse_charges").$type<DecimalValue>(),
  notes: text("notes"),
  createdBy: integer("created_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
}, (table) => ({
  movementDateNotFuture: check("order_movements_movement_date_not_future", sql`${table.movementDate}::date <= CURRENT_DATE`),
  orderIdx: index("order_movements_order_id_idx").on(table.orderId),
}));

export const insertOrderMovementSchema = createInsertSchema(orderMovements, { shippingHandlingDutyCharges: nullableDecimalSchema.optional(), warehouseCharges: nullableDecimalSchema.optional() }).omit({ id: true, createdAt: true });
export type InsertOrderMovement = z.input<typeof insertOrderMovementSchema>;
export type OrderMovement = typeof orderMovements.$inferSelect;

export const orderMovementLines = pgTable("order_movement_lines", {
  id: serial("id").primaryKey(),
  movementId: integer("movement_id").notNull().references(() => orderMovements.id, { onDelete: "cascade" }),
  quantity: numeric("quantity").$type<DecimalValue>().notNull(),
  internalBatchNumber: text("internal_batch_number"),
  externalBatchNumber: text("external_batch_number"),
}, (table) => ({
  quantityPositive: check("order_movement_lines_quantity_positive", sql`${table.quantity} > 0`),
  movementIdx: index("order_movement_lines_movement_id_idx").on(table.movementId),
}));

export const insertOrderMovementLineSchema = createInsertSchema(orderMovementLines, { quantity: positiveStoredQuantitySchema }).omit({ id: true });
export type InsertOrderMovementLine = z.input<typeof insertOrderMovementLineSchema>;
export type OrderMovementLine = typeof orderMovementLines.$inferSelect;

export type ChangeHistory = typeof changeHistory.$inferSelect;

export const documents = pgTable("documents", {
  id: serial("id").primaryKey(),
  tenantId: integer("tenant_id").notNull().references(() => tenants.id),
  chemicalId: integer("chemical_id").references(() => chemicals.id),
  orderId: integer("order_id").references(() => orders.id),
  typeKey: text("type_key").notNull(),
  displayName: text("display_name").notNull(),
  createdBy: integer("created_by").notNull().references(() => users.id),
  createdAt: timestamp("created_at").notNull().defaultNow(),
  currentVersionId: integer("current_version_id").references((): AnyPgColumn => documentVersions.id),
}, (table) => {
  return {
    oneOwner: check("documents_one_owner", sql`(chemical_id IS NOT NULL) <> (order_id IS NOT NULL)`),
    chemicalIdx: index("documents_chemical_id_idx").on(table.chemicalId),
    orderIdx: index("documents_order_id_idx").on(table.orderId),
  };
});

export const insertDocumentSchema = createInsertSchema(documents).omit({
  id: true,
  createdAt: true,
});

export type InsertDocument = z.infer<typeof insertDocumentSchema>;
export type Document = typeof documents.$inferSelect;

export const documentVersions = pgTable("document_versions", {
  id: serial("id").primaryKey(),
  documentId: integer("document_id").notNull().references(() => documents.id, { onDelete: "cascade" }),
  storageKey: text("storage_key").notNull().unique(),
  originalFilename: text("original_filename"),
  mimeType: text("mime_type").notNull(),
  sizeBytes: integer("size_bytes"),
  uploadedBy: integer("uploaded_by").notNull().references(() => users.id),
  uploadedAt: timestamp("uploaded_at").notNull().defaultNow(),
  replacementReason: text("replacement_reason"),
}, (_table) => {
  return {
    sizePositive: check("document_versions_size_positive", sql`size_bytes IS NULL OR size_bytes > 0`),
  };
});

export const insertDocumentVersionSchema = createInsertSchema(documentVersions).omit({
  id: true,
  uploadedAt: true,
});

export type InsertDocumentVersion = z.infer<typeof insertDocumentVersionSchema>;
export type DocumentVersion = typeof documentVersions.$inferSelect;

